/**
 * A sub page link, rendered indented beneath its parent while the user is
 * inside that section. Deliberately not an `IDashboardMenus`: children carry no
 * icon (indentation carries the hierarchy) and cannot nest further, so the type
 * enforces the one-level rule rather than leaving it to convention.
 */
interface IDashboardSubMenu {
	title: string;
	path: string;
	hidden?: boolean;
}

interface IDashboardMenus {
	title: string;
	icon: string;
	path: string;
	description?: string;
	hidden?: boolean;
	children?: IDashboardSubMenu[];
}

/** A whole sidebar menu: named groups, each holding the items it renders. */
export type AppMenu = Record<string, { name: string; routes: IDashboardMenus[] }>;

/**
 * One area of the app, identified by the route prefix its pages live under.
 * `label` names the area to a user; the group names inside `menu` cannot, since
 * every menu declares the same two (`Core`, `General`).
 */
export interface IAppSection {
	prefix: string;
	label: string;
	menu: AppMenu;
}

export const SIDEBAR_EXPAND_WIDTH = 280;
export const SIDEBAR_COLLAPSED_WIDTH = 72;
export const APP_MENU: AppMenu = {
	main: {
		name: 'Core',
		routes: [
			{
				title: '125 Data Dashboard',
				icon: 'LineChart',
				path: '/125data',
				description: '125 Data system dashboard',
			},
			{
				title: 'Depot Dashboard',
				icon: 'Warehouse',
				path: '/depot',
				description: 'Depot system dashboard',
			},
			{
				title: 'Mantle Dashboard',
				icon: 'Layers',
				path: '/mantle',
				description: 'Mantle system dashboard',
			},
		],
	},
	settings: {
		name: 'General',
		routes: [
			{
				path: '/user/profile',
				title: 'User',
				icon: 'UserRound',
				description: 'Manage User',
			},
			// {
			// 	path: '/settings',
			// 	title: 'Settings',
			// 	icon: 'SlidersHorizontal',
			// 	description: 'Settings',
			// },
		],
	},
};

export const ONETWOFIVE_MENU: AppMenu = {
	main: {
		name: 'Core',
		routes: [
			{
				title: 'Dashboard',
				icon: 'LayoutDashboard',
				path: '/125data',
			},
			{
				title: 'Companies',
				icon: 'Building2',
				path: '/125data/companies',
			},
			{
				title: 'Listings',
				icon: 'MapPinned',
				path: '/125data/listings',
				description: 'Look up the locations we manage on listing platforms',
				children: [
					{ title: 'Google managed listings', path: '/125data/listings/google-locations' },
				],
			},
			{
				title: 'Snippets',
				icon: 'TextQuote',
				path: '/125data/snippets',
				description: 'Browse and export the response snippets companies hold',
			},
			{
				title: 'Feedback',
				icon: 'MessageSquare',
				path: '/125data/feedback',
				description: 'Browse company feedback, or pull out its best comments',
				// "All feedback" rather than "Feedback": the path repeats its parent, and
				// a child sharing its parent's label reads as a rendering fault.
				children: [
					{ title: 'All feedback', path: '/125data/feedback/feedback' },
					{ title: 'Top comments', path: '/125data/feedback/top-comments' },
				],
			},
			{
				title: 'Reports',
				icon: 'FileBarChart',
				path: '/125data/reports',
				description: 'Browse the report catalogue and build reports',
				children: [
					{ title: 'Catalogue', path: '/125data/reports/catalogue' },
					{ title: 'Report Builder', path: '/125data/reports/builder' },
				],
			},
			{
				title: 'Chatbots',
				icon: 'Bot',
				path: '/125data/chatbots',
				description: 'Manage Chatbots',
				children: [{ title: 'FAQs', path: '/125data/chatbots/faqs' }],
			},
			{
				// The whole section needs 125.Admin (declared on the route), so role-filtered
				// navigation hides this from a 125.User.
				title: 'Integrations',
				icon: 'KeyRound',
				path: '/125data/integrations',
				description: 'Customer API integrations and what each may receive',
				children: [
					{ title: 'All integrations', path: '/125data/integrations/list' },
					{ title: 'Templates', path: '/125data/integrations/templates' },
				],
			},
		],
	},
	settings: {
		name: 'General',
		routes: [
			{
				path: '/user/profile',
				title: 'User',
				icon: 'Users',
				description: 'Manage User',
			},
			{
				path: '/settings',
				title: 'Settings',
				icon: 'Settings2',
				description: 'Settings',
			},
		],
	},
};

export const DEPOT_MENU: AppMenu = {
	main: {
		// Order is the rendered order: the sidebar maps this array as it stands and
		// applies no sort. The first eight follow the data hierarchy, from the
		// companies that own sites, through the profiles those sites carry, to the
		// reviews and aggregations derived from them, then the reference data that
		// classifies and locates them. The rest is operational tooling and admin,
		// reached for less often. Role filtering runs after ordering, so a viewer
		// who cannot reach an item sees the remainder in the same relative order.
		name: 'Core',
		routes: [
			{
				title: 'Dashboard',
				icon: 'LayoutDashboard',
				path: '/depot',
			},
			{
				title: 'Companies',
				icon: 'Building2',
				path: '/depot/companies',
				description: 'Manage Companies',
				children: [
					{ title: 'All Companies', path: '/depot/companies/all-companies' },
					{ title: 'Browse by Letter', path: '/depot/companies/browse' },
					{ title: 'Add Company', path: '/depot/companies/add-company' },
					{ title: 'Fix Site Naming', path: '/depot/companies/fix-site-naming' },
					{ title: 'Dedupe Sites', path: '/depot/companies/dedupe-sites' },
					{
						title: 'Sync Profile Ownership',
						path: '/depot/companies/sync-profile-ownership',
					},
				],
			},
			{
				title: 'Sites',
				icon: 'Store',
				path: '/depot/sites',
				// Worded against Place Details deliberately: both sections offer a
				// "Search" and the two are easy to confuse. This one reads the
				// companies container, so it covers sites Place Details cannot see.
				description: 'Search all sites, including those with no Place ID',
				// Overview is deliberately absent: the parent route redirects to it,
				// so listing it as a child would give two links to the same page.
				children: [{ title: 'Search', path: '/depot/sites/search' }],
			},
			{
				title: 'Profiles',
				icon: 'BadgeCheck',
				path: '/depot/profiles',
				description: 'Manage Profiles',
				// Single Channels sits here rather than in a section of its own: it
				// reports on the sites linked to exactly one profile. Listed second
				// so the two listing pages sit together ahead of the four actions.
				children: [
					{ title: 'View Profiles', path: '/depot/profiles/view-profiles' },
					{ title: 'Single Channels', path: '/depot/profiles/channels' },
					{ title: 'Add Profile', path: '/depot/profiles/add-profile' },
					{ title: 'Bulk Add Profiles', path: '/depot/profiles/bulk-add-profiles' },
					{ title: 'Enrich Profiles List', path: '/depot/profiles/enrich-profiles-list' },
					{
						title: 'Rebuild Review Counts',
						path: '/depot/profiles/rebuild-review-counts',
					},
				],
			},
			{
				title: 'Reviews',
				icon: 'MessageSquare',
				path: '/depot/reviews',
				description: 'View Reviews',
				children: [
					{ title: 'All Reviews', path: '/depot/reviews/all-reviews' },
					{ title: 'AI Insights', path: '/depot/reviews/insights' },
				],
			},
			{
				title: 'Aggregations',
				icon: 'LineChart',
				path: '/depot/aggregations',
				description: 'Search aggregation snapshots',
				children: [
					{ title: 'Search', path: '/depot/aggregations/search' },
					{ title: 'VS', path: '/depot/aggregations/vs' },
				],
			},
			{
				title: 'Sectors',
				icon: 'NotebookTabs',
				path: '/depot/sectors',
				description: 'Manage Sectors',
				children: [
					{ title: 'View Sectors', path: '/depot/sectors/view-sectors' },
					{ title: 'Add Sector', path: '/depot/sectors/add-sector' },
				],
			},
			{
				title: 'Place Details',
				icon: 'MapPin',
				path: '/depot/place-details',
				description: 'Browse and refresh Google Place Details records',
				// Overview is deliberately absent: the parent route redirects to it,
				// so listing it as a child would give two links to the same page.
				children: [
					{ title: 'Refresh', path: '/depot/place-details/refresh' },
					{ title: 'Backfill', path: '/depot/place-details/backfill' },
					{ title: 'Search', path: '/depot/place-details/search' },
					{ title: 'Map', path: '/depot/place-details/map' },
					{ title: 'Validate CSV', path: '/depot/place-details/validate-csv' },
					{ title: 'Import CSV', path: '/depot/place-details/import-csv' },
					{ title: 'Google Lookup', path: '/depot/place-details/google-lookup' },
				],
			},
			{
				title: 'Fetch Response Profiles',
				icon: 'ArrowDownUp',
				path: '/depot/fetch-response-profiles',
				description: 'Fetch response profiles',
				children: [{ title: 'Profiles', path: '/depot/fetch-response-profiles/profiles' }],
			},
			{
				title: 'Scheduled Jobs',
				icon: 'CalendarClock',
				path: '/depot/schedules',
				description: 'Manage Scheduled Jobs',
				children: [{ title: 'All Jobs', path: '/depot/schedules/all-jobs' }],
			},
			{
				title: 'Clients',
				icon: 'Handshake',
				path: '/depot/clients',
				description: 'Manage Clients',
			},
			{
				title: 'Service Bus',
				icon: 'Send',
				path: '/depot/service-bus',
				description: 'Inspect queues, topics and dead-letter messages',
			},
			{
				title: 'Audit',
				icon: 'ScrollText',
				path: '/depot/audit',
				description: 'Read the audit trail of API actions',
			},
		],
	},
	settings: {
		name: 'General',
		routes: [
			{
				path: '/user/profile',
				title: 'User',
				icon: 'Users',
				description: 'Manage User',
			},
			{
				path: '/settings',
				title: 'Settings',
				icon: 'Settings2',
				description: 'Settings',
			},
		],
	},
};

export const MANTLE_MENU: AppMenu = {
	main: {
		name: 'Core',
		routes: [
			{
				title: 'Dashboard',
				icon: 'LayoutDashboard',
				path: '/mantle',
			},
		],
	},
};

/**
 * The one declaration of the app's sections. The sidebar reads it for the menu
 * it renders and the label under the brand mark; page search reads it for its
 * corpus and its group headings. Adding a section is one entry here rather than
 * an edit in each of those places.
 *
 * Order matters twice: `sectionForPath` returns the first prefix that matches,
 * and search lists its groups in this order. The workspace entry is last and
 * its prefix matches every path, so it acts as the fallback.
 */
export const APP_SECTIONS: IAppSection[] = [
	{ prefix: '/depot', label: 'Depot', menu: DEPOT_MENU },
	{ prefix: '/mantle', label: 'Mantle', menu: MANTLE_MENU },
	{ prefix: '/125data', label: '125 Data', menu: ONETWOFIVE_MENU },
	{ prefix: '/', label: 'Workspace', menu: APP_MENU },
];

/** The section a route path belongs to, falling back to the workspace entry. */
export const sectionForPath = (path: string): IAppSection => {
	return (
		APP_SECTIONS.find((section) => {
			return path.startsWith(section.prefix);
		}) ??
		// Unreachable while the workspace entry's prefix is '/', but a
		// non-optional return beats a cast.
		APP_SECTIONS[APP_SECTIONS.length - 1]!
	);
};
